import { useCallback, useEffect, useRef, useState } from "react";

// Audio-only capture for spoken assessment answers.
//
// DECISION: the InterviewModule audio primitive (`src/utils/audioRecorder.ts`)
// records a COMBINED audio+video (webcam) stream for the LiveKit interview. A
// formative spoken answer should not force a webcam on, so reusing it verbatim
// does not fit. This hook keeps that file's MediaRecorder/getUserMedia pattern
// (echo cancellation, supported-mime probing, blob assembly, track cleanup) but
// requests audio only. The LiveKit agent is not involved.

export type CaptureState = "idle" | "recording" | "recorded" | "error";

function supportedMime(): string {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const t of types) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
  }
  return "audio/webm";
}

export interface AudioCapture {
  state: CaptureState;
  error: string | null;
  audioUrl: string | null;
  blob: Blob | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
}

export function useAudioCapture(): AudioCapture {
  const [state, setState] = useState<CaptureState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const urlRef = useRef<string | null>(null);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This browser does not support microphone access.");
      setState("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const mimeType = supportedMime();
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const out = new Blob(chunksRef.current, { type: mimeType });
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const url = URL.createObjectURL(out);
        urlRef.current = url;
        setBlob(out);
        setAudioUrl(url);
        setState("recorded");
        cleanupStream();
      };
      recorderRef.current = recorder;
      recorder.start();
      setState("recording");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start recording.");
      setState("error");
      cleanupStream();
    }
  }, [cleanupStream]);

  const stop = useCallback(async () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }, []);

  const reset = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    chunksRef.current = [];
    setBlob(null);
    setAudioUrl(null);
    setError(null);
    setState("idle");
  }, []);

  useEffect(() => {
    return () => {
      cleanupStream();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [cleanupStream]);

  return { state, error, audioUrl, blob, start, stop, reset };
}
